'use client';
import { useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useT } from '../lib/i18n';
import { SmartLink } from './SmartLink';
import { Icon } from './Icon';

/**
 * 마이페이지 좌측 메뉴 — 가이드 modify.html 의 `my-nav`(수강·결제·개인정보 3묶음).
 *
 * 아직 만들지 않은 화면은 **눌리지 않게 흐리게** 둔다(`is-soon`) — 링크만 걸어 두면
 * 회원이 404 를 본다. 화면이 생기면 `READY` 에 경로를 더하면 된다.
 * 좁은 화면에서는 가이드처럼 셀렉트 하나로 바뀐다(CSS 가 서로 감춘다).
 */
const GROUPS: { label: string; items: { href: string; label: string }[] }[] = [
  {
    label: 'my.group.learning',
    items: [
      { href: '/my', label: 'my.classroom' },
      { href: '/my/subscription', label: 'my.subscription' },
      /*
       * [1:1 문의]는 여기 두지 않는다(2026-09-02 사용자 지시로 삭제).
       * 게시판은 **관리자가 메뉴로 건다** — 대표 게시판이라 헤더·푸터 메뉴에서 이미 닿는다.
       * 마이페이지에 또 두면 같은 게시판으로 가는 길이 둘이 되고, 그 게시판을 안 쓰는
       * 마켓에서도 이 자리만 남는다.
       */
      { href: '/my/courses/expired', label: 'my.expired' },
    ],
  },
  {
    label: 'my.group.payment',
    items: [
      { href: '/cart', label: 'my.cart' },
      { href: '/my/orders', label: 'my.orders' },
      { href: '/my/wish', label: 'my.wish' },
      { href: '/my/points', label: 'my.points' },
      { href: '/my/coupons', label: 'my.coupons' },
    ],
  },
  {
    label: 'my.group.account',
    items: [
      { href: '/my/subscription/methods', label: 'my.payMethods' },
      { href: '/my/profile', label: 'my.profile' },
      { href: '/my/devices', label: 'my.devices' },
      { href: '/my/sns', label: 'my.sns' },
      /*
       * 설정(말·화면 모드) — 2026-09-05 사용자 지시로 여기 둔다. 로그인 없이도 쓰는 화면이라
       * 푸터·모바일 메뉴에도 길이 있고, 여기는 PC 에서 찾기 쉬우라고 하나 더 낸 길이다.
       */
      { href: '/settings', label: 'set.go' },
      { href: '/my/withdraw', label: 'my.withdraw' },
    ],
  },
];

/** 지금 실제로 있는 화면 — 나머지는 흐리게 둔다(단계가 진행되며 늘어난다) */
const READY = new Set([
  '/my/profile', '/my/withdraw', '/cart', '/my/wish', '/my/orders',
  '/my/points', '/my/coupons', // 2026-08-28
  '/my/subscription', '/my/subscription/methods', // 2026-08-29 구독
  '/my/devices', // 2026-09-02 기기등록정보
  '/settings', // 2026-09-05 말·화면 모드
  '/my', '/my/courses/expired', // 2026-09-07 내 강의실·종료된 강좌
]);

/** 포인트를 안 쓰는 마켓에서 감출 자리 — 현행도 `point_yn` 으로 메뉴째 숨긴다 */
const POINT_ONLY = new Set(['/my/points']);

/**
 * 구독을 안 쓰는 마켓에서 감출 자리(2026-08-29).
 * 포인트와 같은 규칙이다 — **흐리게 두지 않고 아예 그리지 않는다.**
 * 흐린 것은 "곧 생긴다" 는 뜻이고, 이건 그 사이트에 없는 기능이다.
 */
const BILLING_ONLY = new Set(['/my/subscription', '/my/subscription/methods']);

export function MyNav({ pointEnabled = true, billingEnabled = true }: {
  pointEnabled?: boolean;
  billingEnabled?: boolean;
}) {
  const t = useT();
  const path = usePathname();
  const router = useRouter();
  /**
   * 지금 어느 항목에 있나 — **가장 길게 맞는 것**을 고른다. 상세 화면은 주소가 항목과 다르기 때문이다
   * (`/my/courses/16` → 내 강의실 `/my`, `/my/orders/5` → 주문내역 `/my/orders`).
   */
  /**
   * 뒤로 — **브라우저 기록**을 따른다(목록에서 들어왔으면 그 목록으로, 검색 결과에서 왔으면 거기로).
   * 주소를 바로 치고 들어왔거나 새 탭으로 열려 **돌아갈 기록이 없으면** 마이페이지 첫 화면으로 보낸다 —
   * 눌렀는데 아무 일도 안 일어나면 고장으로 보인다.
   */
  const goBack = () => {
    if (window.history.length > 1) window.history.back();
    else router.push('/my');
  };

  const current = useMemo(
    () => [...READY].filter((h) => path === h || path.startsWith(`${h}/`)).sort((a, b) => b.length - a.length)[0] ?? path,
    [path],
  );
  /*
   * 포인트를 안 쓰는 마켓에서는 그 자리를 **아예 그리지 않는다**(현행 `point_yn` 계승).
   * 흐리게 두는 것과 다르다 — 흐린 것은 "곧 생긴다" 는 뜻이고, 이건 그 사이트에 없는 기능이다.
   */
  const hidden = new Set([
    ...(pointEnabled ? [] : POINT_ONLY),
    ...(billingEnabled ? [] : BILLING_ONLY),
  ]);
  const groups = hidden.size === 0
    ? GROUPS
    : GROUPS.map((g) => ({ ...g, items: g.items.filter((it) => !hidden.has(it.href)) }))
      .filter((g) => g.items.length > 0);

  return (
    <>
      {/*
        좁은 화면용 — 가이드가 셀렉트와 목록을 함께 두고 CSS 로 하나만 보여 준다.
        **지금 자리는 `current` 로 고른다**(2026-09-17) — 주소를 그대로 대면 상세 화면(`/my/courses/16`)이
        어느 항목과도 맞지 않아 엉뚱한 첫 줄이 골라진 것처럼 보였다.

        그 줄 **왼쪽에 뒤로가기를 붙박이로** 둔다(2026-09-17 사용자 지시). 좁은 화면에서는 이 선택 상자가
        좌측 메뉴를 대신하는데, **지금 있는 자리를 다시 골라서는 돌아갈 수 없다**(같은 값이라 이동이 일어나지 않는다).
        화면마다 따로 뒤로가기를 그리지 않고 **모든 마이페이지 화면이 같은 자리**를 쓰게 한다 — 찾을 곳이 하나여야 한다.
      */}
      <div className="my-nav-select-row">
        <button type="button" className="my-nav-back" onClick={goBack} aria-label={t('action.back')} title={t('action.back')}>
          <Icon name="chevron-left" size={18} />
        </button>
        <select className="my-nav-select" value={current}
          onChange={(e) => { if (READY.has(e.target.value)) window.location.href = e.target.value; }}>
          {groups.map((g) => (
            <optgroup key={g.label} label={t(g.label)}>
              {g.items.map((it) => (
                <option key={it.href} value={it.href} disabled={!READY.has(it.href)}>
                  {t(it.label)}{READY.has(it.href) ? '' : ` (${t('my.soon')})`}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <nav className="my-nav">
        {groups.map((g) => (
          <div className="my-nav-group" key={g.label}>
            <p className="my-nav-label">{t(g.label)}</p>
            {g.items.map((it) => (
              READY.has(it.href)
                ? (
                  <SmartLink key={it.href} href={it.href} className={`my-nav-link${path === it.href ? ' active' : ''}`}>
                    {t(it.label)}
                  </SmartLink>
                )
                : (
                  <span key={it.href} className="my-nav-link is-soon" title={t('my.soon')}>
                    {t(it.label)}
                  </span>
                )
            ))}
          </div>
        ))}
        {/*
          가이드는 여기 맨 아래에 라이트/다크 토글을 두지만 **걷었다**(2026-09-05 사용자 지시).
          화면 모드는 이제 [설정] 화면이 맡는다 — 같은 값을 두 곳에서 켜면 어느 쪽이 맞는지
          헷갈리고, 휴대폰에서는 이 메뉴가 통째로 숨어 반쪽만 쓸 수 있었다.
        */}
      </nav>
    </>
  );
}
